import * as React from 'react'
import first from 'lodash/first'
import {useSafeCallbackExtraDeps, unCallbackFn} from '@freckle/react-hooks'
import {Dialog, DialogDismiss, DialogHeading} from '@ariakit/react'
import * as dialogStyles from '@frontrow/console/src/components/dialog/dialog.module.scss'
import {Button} from '@frontrow/console/src/components/button'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'
import {mthen} from '@freckle/maybe'

import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import {
  type BasicSchoolAdminT,
  type DetailedSchoolAdminT
} from '@frontrow/console/src/models/school-admin'
import SchoolAdminCreateForm from '@frontrow/console/src/components/pages/school-admins-page/school-admin-create-button-modal/school-admin-create-form'
import {postSchoolAdmin} from '@frontrow/console/src/helpers/api-school-admins'
import {useModalForm} from '@frontrow/console/src/hooks/use-modal-form'

type Props = {
  schoolAdminNewAdmin: (newSchoolAdmin: DetailedSchoolAdminT) => void
  updateSchoolAdminSchools: (
    schoolAdminId: number,
    addSchoolIds: Array<number>,
    deleteSchoolIds: Array<number>
  ) => Promise<void>
}

function SchoolAdminCreateButtonModal(props: Props): React.ReactElement {
  const {updateSchoolAdminSchools, schoolAdminNewAdmin} = props
  const {isOpen, open, close} = useModalForm()
  const [errors, setErrors] = React.useState<Array<string>>([])
  const [resetInput, setResetInput] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)

  const postSchoolAdminCallback = useSafeCallbackExtraDeps<
    (
      newSchoolAdminData: BasicSchoolAdminT,
      schoolIds: Array<number>,
      addAnother: boolean
    ) => Promise<void>,
    {
      updateSchoolAdminSchools: (
        schoolAdminId: number,
        addSchoolIds: Array<number>,
        deleteSchoolIds: Array<number>
      ) => Promise<void>
      schoolAdminNewAdmin: (newSchoolAdmin: DetailedSchoolAdminT) => void
      close: () => void
    }
  >(
    ({updateSchoolAdminSchools, schoolAdminNewAdmin, close}) =>
      async (
        newSchoolAdminData: BasicSchoolAdminT,
        schoolIds: Array<number>,
        addAnother: boolean
      ) => {
        setIsLoading(true)
        setResetInput(false)
        setErrors([])

        try {
          const newSchoolAdmin = await postSchoolAdmin(newSchoolAdminData)

          if (schoolIds.length > 0) {
            await updateSchoolAdminSchools(newSchoolAdmin.id, schoolIds, [])
          }

          schoolAdminNewAdmin(newSchoolAdmin)
          setIsLoading(false)
          if (!addAnother) {
            close()
          }
          setErrors([])
          setResetInput(true)
        } catch (error) {
          const apiError = firstResponseApiError(error)
          setIsLoading(false)
          setErrors([apiError.message])
        }
      },
    [],
    {
      updateSchoolAdminSchools: {value: updateSchoolAdminSchools, comparator: () => true},
      schoolAdminNewAdmin: {value: schoolAdminNewAdmin, comparator: () => true},
      close: {value: close, comparator: () => true}
    }
  )

  const errorNode = mthen(first(errors), error => <ErrorAlert>{error}</ErrorAlert>)

  return (
    <React.Fragment>
      <Button onClick={open}>Add a School Admin</Button>
      <Dialog
        open={isOpen}
        onClose={close}
        backdrop={<div className={dialogStyles.backdrop} />}
        className={dialogStyles.dialog}
        portal
        unmountOnHide
        hideOnEscape
        hideOnInteractOutside={false}
      >
        <div className={dialogStyles.header}>
          <DialogHeading className={dialogStyles.title}>Add a School Admin</DialogHeading>
          <DialogDismiss className={dialogStyles.closeButton}>
            <span aria-hidden="true">&times;</span>
          </DialogDismiss>
        </div>
        <div className={dialogStyles.body}>
          {errorNode}
          <SchoolAdminCreateForm
            isLoading={isLoading}
            onSubmit={unCallbackFn(postSchoolAdminCallback)}
            onCancel={close}
            resetInput={resetInput}
          />
        </div>
      </Dialog>
    </React.Fragment>
  )
}

export default SchoolAdminCreateButtonModal
